/* eslint-disable max-lines */
import _ from 'underscore'
import 'event-source-polyfill'
import Environment from 'helpers/environment'
import Cookies from 'helpers/cookies'
import fetchCognitoToken from 'helpers/aws'

const PULSE_INTERVAL = 3 * 60 * 1000 // send a heartbeat pulse to the server every 3 minutes
class NexiaStream {
  static initClass () {
    NexiaStream.WAITING = EventSource.WAITING
    NexiaStream.CONNECTING = EventSource.CONNECTING
    NexiaStream.OPEN = EventSource.OPEN
    NexiaStream.CLOSED = EventSource.CLOSED

    this.prototype._eventListeners = []
  }

  eventListeners () { return this._eventListeners.slice(0) }

  constructor (url, options, streamSocketId) {
    this.eventListeners = this.eventListeners.bind(this)
    this._ensureSafe = this._ensureSafe.bind(this)
    this._refreshConnection = this._refreshConnection.bind(this)
    if (streamSocketId == null) { streamSocketId = undefined }
    if (NexiaStream._instance) {
      throw new Error('Singleton Class: use NexiaStream.instance() instead of constructor')
    }

    NexiaStream._instance = this
    Cookies.setStreamId()
    const uuid = Cookies.uuid()
    url = (url || Environment.backendApiURL() + '/stream/') + `?streamSocketId=${uuid}`
    options ||= {}
    options.withCredentials = true

    this.streamSocketId = streamSocketId ?? uuid
    this.pendingConnectCallbacks = []
    this.connected = false
    this.subscriptions = {}
    this.originalUrl = url
    this.originalOptions = options
    options.headers = options.headers || {}

    this._initializeConnection()

    // Set up token refresh interval (every 4 minutes to be safe)
    this.tokenRefreshInterval = setInterval(this._refreshConnection, 4 * 60 * 1000)
  }

  async _initializeConnection () {
    this.eventSource = fetchCognitoToken().then(async (idToken) => {
      if (Environment.isAWSCognito() && idToken) {
        this.originalOptions.headers = {
          ...this.originalOptions.headers,
          'COGNITO-Authorization': `${idToken}`
        }
      }
      const eventSource = new NexiaStream.eventSourceClass(this.originalUrl, this.originalOptions) // eslint-disable-line new-cap
      eventSource.dispatchEvent = this.safeDispatchEvent(eventSource.dispatchEvent, this._ensureSafe)

      this.addEventListener('open', this._onOpen)
      this.addEventListener('message', this._onMessage) // catchall for events without a type
      this.addEventListener('close', this._onClose)
      this.addEventListener('error', this._onError)

      this._startHeartbeat()

      return eventSource
    })
  }

  async _refreshConnection () {
    try {
      console.log('Refreshing NexiaStream connection with new token...')

      // Get fresh token
      const newToken = await fetchCognitoToken()

      if (Environment.isAWSCognito() && newToken) {
        // Close existing connection
        if (this.eventSource) {
          const existingEventSource = await this.eventSource
          existingEventSource.close()
        }

        // Stop existing heartbeat
        this._stopHeartbeat()

        // Update headers with new token
        this.originalOptions.headers = {
          ...this.originalOptions.headers,
          'COGNITO-Authorization': `${newToken}`
        }

        // Reset connection state
        this.connected = false

        // Re-initialize connection with new token
        await this._initializeConnection()

        console.log('NexiaStream connection refreshed successfully')
      }
    } catch (error) {
      console.error('Error refreshing NexiaStream connection:', error)
    }
  }

  async addEventListener (type, callback) {
    this._eventListeners.push({ type, callback })
    return (await this.eventSource).addEventListener(type, callback)
  }

  async removeEventListener (type, callback) {
    this._eventListeners = _.reject(this._eventlisteners, event => (event.type === type) && (event.callback === callback))
    return (await this.eventSource).removeEventListener(type, callback)
  }

  removeAllEventListeners () {
    return _.each(this._eventListeners, event => {
      return this.removeEventListener(event.type, event.callback)
    })
  }

  safeDispatchEvent (originalDispatcher, ensureSafe) {
    return function () {
      ensureSafe.apply(this, arguments)
      return originalDispatcher.apply(this, arguments)
    }
  }

  whenConnected (onConnectCallback) {
    if (!this.connected) {
      return this.pendingConnectCallbacks.push(onConnectCallback)
    } else {
      return onConnectCallback()
    }
  }

  async subscribeTo (url) {
    const idToken = await fetchCognitoToken()

    $.ajax(this._urlWithStreamId(url), {
      crossDomain: true,
      headers: {
        ...(Environment.isAWSCognito() && { 'COGNITO-Authorization': `${idToken}` })
      },
      xhrFields: {
        withCredentials: true
      }
    })
  }

  async unsubscribeFrom (url) {
    const idToken = await fetchCognitoToken()

    $.ajax(this._urlWithStreamId(url), {
      crossDomain: true,
      xhrFields: {
        withCredentials: true
      },
      headers: {
        ...(Environment.isAWSCognito() && { 'COGNITO-Authorization': `${idToken}` })
      },
      type: 'DELETE'
    })
  }

  setConnected (value) {
    if (value == null) { value = true }
    this.connected = value
  }

  runPendingConnectCallbacks () {
    _.each(this.pendingConnectCallbacks, callback => callback())
    this.pendingConnectCallbacks = []
  }

  streamId () {
    return this.streamSocketId
  }

  _startHeartbeat () {
    if (this.pulseInterval) throw new Error('The heartbeat has already been started.')

    this.pulseInterval =
      setInterval(async () => {
        const idToken = await fetchCognitoToken()

        $.ajax(
          this._urlWithStreamId(Environment.backendApiURL() + '/stream/pulse'),
          {
            headers: {
              ...(Environment.isAWSCognito() && { 'COGNITO-Authorization': `${idToken}` })
            },
            xhrFields: {
              withCredentials: true
            },
            crossDomain: true
          }
        )
      }, window.runningSpecs ? 0 : PULSE_INTERVAL)
  }

  _stopHeartbeat () {
    clearInterval(this.pulseInterval)

    this.pulseInterval = null
  }

  _urlWithStreamId (url) {
    return url + `?streamSocketId=${this.streamId()}`
  }

  _ensureSafe (e) {
    return this._debugStream(e)
  }
  // if event.origin != "http://dealer.mynexia" # is it possible to check origin for eventsource?
  // throw new StreamSecurityError("Cross Site Scripting Attack Detected: Your IP has been logged.")
  // if(checksum(event.data) != event.checksum)  #is asymetic key signing possible in JS? how concerned are we about this?
  //   throw new StreamSecurityError("This message has been tapered with. Your IP has been logged.")

  async _close () {
    return (await this.eventSource).close()
  }

  _onError () {
    return (typeof NexiaStream.errorCallback === 'function' ? NexiaStream.errorCallback() : undefined)
  }

  _onOpen () {
    NexiaStream.instance().setConnected()
    return NexiaStream.instance().runPendingConnectCallbacks()
  }

  _onMessage () { // (e) {
    // don't log anything to console
    // console.log('no event for onMessage') unless e
    // console.log('NexiaStream: onMessage catchall for events without a type') unless e.type
    // console.log("lastId: #{e.lastEventId}") #eventstream message format
    // console.log("type: #{e.type}")
    // console.log("data: #{e.data}")
  }

  _onClose () {
    return NexiaStream.instance().setConnected(false)
  }

  _debugStream (e) {
    return this._onMessage(e)
  }
}
NexiaStream.initClass()

NexiaStream.eventSourceClass = EventSourcePolyfill // eslint-disable-line no-undef

NexiaStream.instance = function (url, options) {
  if (!NexiaStream._instance) {
    new NexiaStream(url, options) // eslint-disable-line no-new
  }

  return NexiaStream._instance
}

NexiaStream.reset = function () {
  if (NexiaStream._instance) {
    NexiaStream._instance._close()
    NexiaStream._instance.removeAllEventListeners()
    NexiaStream._instance._stopHeartbeat()

    // Clean up token refresh interval
    if (NexiaStream._instance.tokenRefreshInterval) {
      clearInterval(NexiaStream._instance.tokenRefreshInterval)
      NexiaStream._instance.tokenRefreshInterval = null
    }

    NexiaStream._instance = null
  }
}

NexiaStream.errorCallback = null

export default NexiaStream
